import { useCallback, useEffect, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import {
  AlertCircle, Check, Copy, Download, KeyRound, Loader2, ShieldCheck, Smartphone,
} from 'lucide-react';
import {
  confirmarEnrolamiento,
  iniciarEnrolamiento,
  verificarCodigo,
  type DatosEnrolamiento,
} from '../../services/dosFactoresService';
import styles from './SegundoFactor.module.css';

const LARGO = 6;

/* ══════════════════════════════════════════════════════════════════════════
   Casilleros del código
   ══════════════════════════════════════════════════════════════════════════ */

function CasillasCodigo({
  valor, onChange, onCompleto, deshabilitado, error,
}: {
  valor: string[];
  onChange: (v: string[]) => void;
  onCompleto: (codigo: string) => void;
  deshabilitado: boolean;
  error: boolean;
}) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => { refs.current[0]?.focus(); }, []);

  const escribir = (i: number, char: string) => {
    const limpio = char.replace(/\D/g, '');
    if (!limpio && char !== '') return;
    const siguiente = [...valor];
    siguiente[i] = limpio.slice(-1);
    onChange(siguiente);
    if (limpio && i < LARGO - 1) refs.current[i + 1]?.focus();
    const completo = siguiente.join('');
    if (completo.length === LARGO) onCompleto(completo);
  };

  return (
    <div className={`${styles.casillas} ${error ? styles.casillasError : ''}`}>
      {Array.from({ length: LARGO }, (_, i) => (
        <input
          key={i}
          ref={(el) => { refs.current[i] = el; }}
          className={styles.casilla}
          type="text"
          inputMode="numeric"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          maxLength={1}
          value={valor[i] ?? ''}
          disabled={deshabilitado}
          aria-label={`Dígito ${i + 1} de ${LARGO}`}
          onChange={(e) => escribir(i, e.target.value)}
          onKeyDown={(e) => {
            // Borrar en una casilla vacía retrocede: es lo que la gente espera
            // y sin ello hay que pulsar dos veces por dígito.
            if (e.key === 'Backspace' && !valor[i] && i > 0) refs.current[i - 1]?.focus();
            if (e.key === 'ArrowLeft' && i > 0) refs.current[i - 1]?.focus();
            if (e.key === 'ArrowRight' && i < LARGO - 1) refs.current[i + 1]?.focus();
          }}
          onPaste={(e) => {
            e.preventDefault();
            const pegado = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, LARGO);
            if (!pegado) return;
            const siguiente = Array(LARGO).fill('');
            pegado.split('').forEach((c, k) => { siguiente[k] = c; });
            onChange(siguiente);
            if (pegado.length === LARGO) onCompleto(pegado);
            else refs.current[pegado.length]?.focus();
          }}
        />
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   Códigos de respaldo
   ══════════════════════════════════════════════════════════════════════════ */

function CodigosRespaldo({ codigos, onListo }: { codigos: string[]; onListo: () => void }) {
  const [copiado, setCopiado] = useState(false);
  const [confirmado, setConfirmado] = useState(false);

  const texto = [
    'Códigos de respaldo — Portal de Teleconsulta MINSA',
    'Cada código sirve UNA sola vez. Guárdelos en un lugar seguro.',
    '',
    ...codigos,
  ].join('\n');

  const descargar = () => {
    const url = URL.createObjectURL(new Blob([texto], { type: 'text/plain' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'codigos-respaldo-minsa.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className={styles.panel}>
      <ShieldCheck className={styles.iconoOk} size={40} aria-hidden="true" />
      <h2 className={styles.titulo}>Listo. Guarde sus códigos de respaldo</h2>
      <p className={styles.texto}>
        Si pierde el teléfono, estos códigos son la única forma de entrar sin ayuda de un
        administrador. Cada uno sirve <strong>una sola vez</strong> y no se volverán a mostrar.
      </p>

      <ul className={styles.listaCodigos}>
        {codigos.map((c) => <li key={c}>{c}</li>)}
      </ul>

      <div className={styles.filaBotones}>
        <button type="button" className={styles.btnSecundario} onClick={descargar}>
          <Download size={15} aria-hidden="true" /> Descargar
        </button>
        <button
          type="button"
          className={styles.btnSecundario}
          onClick={() => {
            navigator.clipboard?.writeText(texto);
            setCopiado(true);
            setTimeout(() => setCopiado(false), 2000);
          }}
        >
          {copiado ? <Check size={15} aria-hidden="true" /> : <Copy size={15} aria-hidden="true" />}
          {copiado ? 'Copiado' : 'Copiar'}
        </button>
      </div>

      <label className={styles.confirmacion}>
        <input
          type="checkbox"
          checked={confirmado}
          onChange={(e) => setConfirmado(e.target.checked)}
        />
        <span>Los guardé en un lugar seguro</span>
      </label>

      <button
        type="button"
        className={styles.btnPrimario}
        disabled={!confirmado}
        onClick={onListo}
      >
        Continuar al portal
      </button>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   Panel principal
   ══════════════════════════════════════════════════════════════════════════ */

export interface SegundoFactorProps {
  desafioId: string;
  /** 'codigo' = ya enrolado, pedir el código. 'enrolar' = configurar por primera vez. */
  modo: 'codigo' | 'enrolar';
  correo: string;
  onListo: () => void;
  onCancelar: () => void;
}

export function SegundoFactor({ desafioId, modo, correo, onListo, onCancelar }: SegundoFactorProps) {
  const [fase, setFase] = useState<'codigo' | 'qr' | 'respaldo'>(modo === 'codigo' ? 'codigo' : 'qr');
  const [digitos, setDigitos] = useState<string[]>(Array(LARGO).fill(''));
  const [respaldoManual, setRespaldoManual] = useState('');
  const [usarRespaldo, setUsarRespaldo] = useState(false);
  const [enrolamiento, setEnrolamiento] = useState<DatosEnrolamiento | null>(null);
  const [codigosRespaldo, setCodigosRespaldo] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const enVuelo = useRef(false);

  // Se genera el secreto al entrar en la fase de enrolamiento.
  useEffect(() => {
    if (fase !== 'qr' || enrolamiento) return;
    let vivo = true;
    iniciarEnrolamiento(desafioId)
      .then((d) => { if (vivo) setEnrolamiento(d); })
      .catch((e) => { if (vivo) setError(e instanceof Error ? e.message : 'No se pudo preparar el enrolamiento.'); });
    return () => { vivo = false; };
  }, [fase, desafioId, enrolamiento]);

  const limpiar = () => { setDigitos(Array(LARGO).fill('')); setTimeout(() => {
    document.querySelector<HTMLInputElement>(`.${styles.casilla}`)?.focus();
  }, 50); };

  /** Canjea el desafío por una sesión. */
  const enviarCodigo = useCallback(async (codigo: string) => {
    if (enVuelo.current) return;
    enVuelo.current = true;
    setOcupado(true);
    setError('');
    try {
      await verificarCodigo(desafioId, codigo);
      onListo();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo verificar el código.');
      limpiar();
    } finally {
      enVuelo.current = false;
      setOcupado(false);
    }
  }, [desafioId, onListo]);

  /** Confirma el enrolamiento: valida el primer código y entrega los respaldos. */
  const confirmar = useCallback(async (codigo: string) => {
    if (enVuelo.current) return;
    enVuelo.current = true;
    setOcupado(true);
    setError('');
    try {
      const { codigosRespaldo: cods } = await confirmarEnrolamiento(codigo, desafioId);
      setCodigosRespaldo(cods);
      setFase('respaldo');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'El código no coincide.');
      limpiar();
    } finally {
      enVuelo.current = false;
      setOcupado(false);
    }
  }, [desafioId]);

  if (fase === 'respaldo') {
    return <CodigosRespaldo codigos={codigosRespaldo} onListo={onListo} />;
  }

  /* ── Enrolamiento ── */
  if (fase === 'qr') {
    return (
      <div className={styles.panel}>
        <Smartphone className={styles.icono} size={36} aria-hidden="true" />
        <h2 className={styles.titulo}>Configure su segundo paso</h2>
        <p className={styles.texto}>
          Escanee este código con <strong>Google Authenticator</strong>, Authy, 1Password o
          Microsoft Authenticator. La aplicación generará un número de seis dígitos que cambia cada
          treinta segundos.
        </p>

        {enrolamiento ? (
          <>
            <div className={styles.qr}>
              <QRCodeSVG value={enrolamiento.uri} size={188} level="M" marginSize={2} />
            </div>
            <details className={styles.manual}>
              <summary>No puedo escanear el código</summary>
              <p>Agregue la cuenta a mano con esta clave:</p>
              <code className={styles.secreto}>
                {enrolamiento.secreto.match(/.{1,4}/g)?.join(' ')}
              </code>
              <p className={styles.textoChico}>Cuenta: {correo} · Tipo: basada en tiempo</p>
            </details>

            <p className={styles.texto}>Ahora escriba el código que muestra la aplicación:</p>
            <CasillasCodigo
              valor={digitos}
              onChange={setDigitos}
              onCompleto={confirmar}
              deshabilitado={ocupado}
              error={!!error}
            />
          </>
        ) : (
          <div className={styles.cargando}><Loader2 className={styles.girando} size={22} /> Preparando…</div>
        )}

        {error && <p className={styles.error}><AlertCircle size={15} aria-hidden="true" /> {error}</p>}
        <button type="button" className={styles.btnTexto} onClick={onCancelar}>Cancelar</button>
      </div>
    );
  }

  /* ── Pedir el código ── */
  return (
    <div className={styles.panel}>
      <KeyRound className={styles.icono} size={36} aria-hidden="true" />
      <h2 className={styles.titulo}>Código de verificación</h2>
      <p className={styles.texto}>
        Abra su aplicación de autenticación y escriba el código de seis dígitos para
        <br /><strong>{correo}</strong>
      </p>

      {usarRespaldo ? (
        <form
          className={styles.formRespaldo}
          onSubmit={(e) => { e.preventDefault(); enviarCodigo(respaldoManual); }}
        >
          <input
            className={styles.inputRespaldo}
            value={respaldoManual}
            onChange={(e) => { setRespaldoManual(e.target.value.toUpperCase()); setError(''); }}
            placeholder="XXXX-XXXX"
            autoComplete="off"
            autoFocus
            disabled={ocupado}
            aria-label="Código de respaldo"
          />
          <button type="submit" className={styles.btnPrimario} disabled={ocupado || respaldoManual.length < 8}>
            {ocupado ? 'Verificando…' : 'Verificar'}
          </button>
        </form>
      ) : (
        <CasillasCodigo
          valor={digitos}
          onChange={setDigitos}
          onCompleto={enviarCodigo}
          deshabilitado={ocupado}
          error={!!error}
        />
      )}

      {ocupado && !usarRespaldo && (
        <p className={styles.cargando}><Loader2 className={styles.girando} size={16} /> Verificando…</p>
      )}
      {error && <p className={styles.error}><AlertCircle size={15} aria-hidden="true" /> {error}</p>}

      <button
        type="button"
        className={styles.btnTexto}
        onClick={() => { setUsarRespaldo(!usarRespaldo); setError(''); }}
      >
        {usarRespaldo ? 'Usar el código de la aplicación' : 'Perdí mi teléfono — usar un código de respaldo'}
      </button>
      <button type="button" className={styles.btnTexto} onClick={onCancelar}>Volver</button>
    </div>
  );
}
